
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { COVERAGE_CHOICE_CITATION_GUID } from "../../../utilities/constants";
import { transformCitationLanguage } from "../../../utilities/helpers";

const ObservationHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: '#1976d2',
    marginBottom: 16,
    marginTop: 24,
    padding: '10px 16px',
    backgroundColor: '#e3f2fd',
    borderLeft: '4px solid #1976d2',
    borderRadius: '0 4px 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&:first-of-type': {
        marginTop: 0,
    }
}));

const CitationItem = styled(Box)(({ theme }) => ({
    marginBottom: 32,
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}));

const CitationTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.1rem',
    color: '#333',
    marginBottom: 20,
    paddingBottom: 8,
    borderBottom: '2px solid #f0f0f0',
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontWeight: '600',
    fontSize: '1rem',
    color: '#444',
    marginTop: 16,
    marginBottom: 8,
}));

const SectionContent = styled(Box)(({ theme }) => ({
    marginBottom: 16,
    padding: '12px',
    backgroundColor: '#fafafa',
    borderRadius: '4px',
    border: '1px solid #f0f0f0',
    '& p': {
        margin: 0,
        lineHeight: 1.6,
    },
    '& div': {
        lineHeight: 1.6,
    }
}));

const CitationReport = ({ inspection, inspectionDetails, b503Operations = [], a503Operations = [] }) => {
    const protocolQualities = inspectionDetails || [];
    const systemChoicesObservation = inspection?.system_choice_observation || [];
    const filteredQuestionCitations = [];

    systemChoicesObservation.forEach((systemChoiceObservation) => {
        const perSystemChoice = protocolQualities
            .filter((protocolQuality) =>
                protocolQuality.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID &&
                systemChoiceObservation.code_table_uid === protocolQuality.detail?.systemChoice
            )
            .sort((a, b) => (a.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER));
        filteredQuestionCitations.push(...perSystemChoice);
    });

    const observation503B = (b503Operations || [])
        .filter((operation) => operation.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID)
        .map((operation, index) => ({
            code: operation.code || `503B-${operation.inspection_503b_operation_uid}-${index}`,
            detail: {
                citationLanguage: operation.description || operation.code || "503B Operation",
                citationSpecifically: operation.detail?.citationSpecifically || "",
                citationSupporting: operation.detail?.citationSupporting || "",
                citationDiscussion: operation.detail?.citationDiscussion || "",
                citationCapa: operation.detail?.citationCapa || "",
                sortOrder: operation.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER
            }
        }))
        .sort((a, b) => (a.detail.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.detail.sortOrder ?? Number.MAX_SAFE_INTEGER));

    const observation503A = (a503Operations || [])
        .filter((operation) => operation.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID)
        .map((operation, index) => ({
            code: operation.code || `503A-${operation.inspection_503a_operation_uid}-${index}`,
            detail: {
                citationLanguage: operation.description || operation.code || "503A Operation",
                citationSpecifically: operation.detail?.citationSpecifically || "",
                citationSupporting: operation.detail?.citationSupporting || "",
                citationDiscussion: operation.detail?.citationDiscussion || "",
                citationCapa: operation.detail?.citationCapa || "",
                sortOrder: operation.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER
            }
        }))
        .sort((a, b) => (a.detail.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.detail.sortOrder ?? Number.MAX_SAFE_INTEGER));

    const allObservationItems = [...filteredQuestionCitations, ...observation503B, ...observation503A];

    if (!allObservationItems || allObservationItems.length === 0) {
        return (
            <Box sx={{ padding: 2, textAlign: 'center', color: '#666' }}>
                <Typography variant="body1">No observations or major findings to report.</Typography>
            </Box>
        );
    }
    
    return (
        <Box>
            {allObservationItems.map((filteredQuestionCitation, index) => (
                <Box key={filteredQuestionCitation?.code}>
                    <ObservationHeader>
                        Observation {index + 1}
                    </ObservationHeader>
                    <CitationItem>
                        <CitationTitle>
                            {transformCitationLanguage(filteredQuestionCitation?.detail?.citationLanguage)}
                        </CitationTitle>
                    
                    {filteredQuestionCitation?.detail?.citationSpecifically && (
                        <>
                            <SectionLabel>Specifically:</SectionLabel>
                            <SectionContent>
                                <div
                                    style={{ lineHeight: "1.6" }}
                                    dangerouslySetInnerHTML={{
                                        __html: filteredQuestionCitation?.detail?.citationSpecifically || "",
                                    }}
                                />
                            </SectionContent>
                        </>
                    )}
                    
                    {filteredQuestionCitation?.detail?.citationSupporting && (
                        <>
                            <SectionLabel>Supporting Evidence:</SectionLabel>
                            <SectionContent>
                                <div
                                    style={{ lineHeight: "1.6" }}
                                    dangerouslySetInnerHTML={{
                                        __html: filteredQuestionCitation?.detail?.citationSupporting || "",
                                    }}
                                />
                            </SectionContent>
                        </>
                    )}
                    
                    {filteredQuestionCitation?.detail?.citationDiscussion && (
                        <>
                            <SectionLabel>Recommendation:</SectionLabel>
                            <SectionContent>
                                <div
                                    style={{ lineHeight: "1.6" }}
                                    dangerouslySetInnerHTML={{
                                        __html: filteredQuestionCitation?.detail?.citationDiscussion || "",
                                    }}
                                />
                            </SectionContent>
                        </>
                    )}
                    
                    {filteredQuestionCitation?.detail?.citationCapa && (
                        <>
                            <SectionLabel>CAPA:</SectionLabel>
                            <SectionContent>
                                <div
                                    style={{ lineHeight: "1.6" }}
                                    dangerouslySetInnerHTML={{
                                        __html: filteredQuestionCitation?.detail?.citationCapa || "",
                                    }}
                                />
                            </SectionContent>
                        </>
                    )}
                    </CitationItem>
                </Box>
            ))}
        </Box>
    );
};

export default CitationReport;
